import React, { memo } from "react";
import { cn } from "../ui";

/**
 * QuestionPalette — the numbered grid every Indian candidate already knows from
 * NTA and TCS iON, in the same five states.
 *
 * Kept deliberately quiet: one brand fill for "answered" (the same fill as a
 * chosen option, so done reads as done), amber for "marked for review", red ink
 * for "seen but not answered", and neutral for everything untouched. Every cell
 * also says its state in words for a screen reader, and "answered & marked"
 * carries a corner dot, so no state is told apart by colour alone.
 *
 * In the right rail on desktop; in a sheet on a phone and in the review screen.
 */

export const paletteState = (answer, idx, currentIndex, visited) => {
  const answered = !!answer?.isAnswered;
  const marked = !!answer?.isFlagged;
  if (answered && marked) return "answered-marked";
  if (marked) return "marked";
  if (answered) return "answered";
  if (visited.has(idx) || idx === currentIndex) return "not-answered";
  return "not-visited";
};

export const STATE_LABEL = {
  answered: "Answered",
  "not-answered": "Not answered",
  "not-visited": "Not visited",
  marked: "Marked for review",
  "answered-marked": "Answered & marked",
};

const LEGEND_ORDER = ["answered", "not-answered", "not-visited", "marked", "answered-marked"];

const STATE_CLASS = {
  answered: "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg",
  "answered-marked": "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg",
  marked: "tw-border-warning tw-bg-warning/15 tw-text-warning-fg",
  "not-answered": "tw-border-danger/50 tw-bg-surface-raised tw-text-danger-fg",
  "not-visited": "tw-border-input-line tw-bg-surface-raised tw-text-content-muted",
};

export function paletteCounts(answers, currentIndex, visited) {
  const seen = (idx) => visited.has(idx) || idx === currentIndex;
  return {
    answered: answers.filter((a) => a.isAnswered && !a.isFlagged).length,
    answeredMarked: answers.filter((a) => a.isAnswered && a.isFlagged).length,
    marked: answers.filter((a) => a.isFlagged && !a.isAnswered).length,
    notAnswered: answers.filter((a, i) => !a.isAnswered && !a.isFlagged && seen(i)).length,
    notVisited: answers.filter((a, i) => !a.isAnswered && !a.isFlagged && !seen(i)).length,
    total: answers.length,
    totalAnswered: answers.filter((a) => a.isAnswered).length,
  };
}

const LEGEND_COUNT = {
  answered: (c) => c.answered,
  "not-answered": (c) => c.notAnswered,
  "not-visited": (c) => c.notVisited,
  marked: (c) => c.marked,
  "answered-marked": (c) => c.answeredMarked,
};

/** The small square that stands for a state — in the legend, and nowhere else. */
function Swatch({ state }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "tw-relative tw-inline-block tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-rounded-[4px] tw-border tw-border-solid",
        STATE_CLASS[state],
      )}
    >
      {state === "answered-marked" && (
        <span className="tw-absolute tw--right-0.5 tw--top-0.5 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-warning" />
      )}
    </span>
  );
}

/**
 * The five states with how many questions are in each. `inline` drops the
 * counts and runs the key along one line — for the review sheet, whose tiles
 * already give the numbers.
 */
export function PaletteLegend({ counts, inline = false, className = "" }) {
  if (inline) {
    return (
      <ul className={cn("tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-5 tw-gap-y-2 tw-p-0", className)}>
        {LEGEND_ORDER.map((s) => (
          <li key={s} className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-content-muted">
            <Swatch state={s} />
            {STATE_LABEL[s]}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className={cn("tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0", className)}>
      {LEGEND_ORDER.map((s) => (
        <li key={s} className="tw-flex tw-items-center tw-gap-2.5 tw-text-[0.8125rem] tw-text-content-muted">
          <Swatch state={s} />
          <span className="tw-flex-1">{STATE_LABEL[s]}</span>
          <span className="tw-font-medium tw-tabular-nums tw-text-content">{LEGEND_COUNT[s](counts)}</span>
        </li>
      ))}
    </ul>
  );
}

// Timed sections in order: where each group stands, beside its title.
const GROUP_STATUS = { done: "Finished", locked: "Opens later" };

function QuestionPalette({ answers, currentIndex, visited, onNavigate, sections = [] }) {
  // A sectioned paper is grouped, so a candidate can see "three left in
  // Aptitude" without counting cells. On a paper whose sections run in order,
  // only the open section's cells can be pressed.
  const groups = sections.length
    ? sections.map((s) => ({ title: s.title, start: s.start, end: s.end, status: s.status }))
    : [{ title: null, start: 0, end: answers.length }];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {groups.map((g, gi) => (
        <div key={`${g.title || "all"}-${gi}`} className="tw-flex tw-flex-col tw-gap-2">
          {g.title && (
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
              <span>{g.title}</span>
              <span className="tw-font-medium tw-normal-case tw-tracking-normal tw-tabular-nums tw-text-content-subtle">
                {GROUP_STATUS[g.status]
                  || `${answers.slice(g.start, g.end).filter((a) => a.isAnswered).length}/${g.end - g.start} answered`}
              </span>
            </div>
          )}
          <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(2.5rem,1fr))] tw-gap-2">
            {answers.slice(g.start, g.end).map((a, i) => {
              const idx = g.start + i;
              const state = paletteState(a, idx, currentIndex, visited);
              const current = idx === currentIndex;
              const closed = g.status === "done" || g.status === "locked";
              return (
                <button
                  type="button"
                  key={a.question || idx}
                  onClick={() => onNavigate(idx)}
                  disabled={closed}
                  aria-label={`Question ${idx + 1}, ${STATE_LABEL[state]}${closed ? `, ${GROUP_STATUS[g.status].toLowerCase()}` : ""}`}
                  aria-current={current ? "true" : undefined}
                  className={cn(
                    "tw-relative tw-flex tw-h-10 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-solid tw-p-0",
                    "tw-text-sm tw-font-medium tw-tabular-nums tw-transition-[box-shadow,background-color] tw-focus-ring",
                    closed ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer",
                    STATE_CLASS[state],
                    current && "tw-ring-2 tw-ring-primary tw-ring-offset-2 tw-ring-offset-surface-raised",
                  )}
                >
                  {idx + 1}
                  {state === "answered-marked" && (
                    <span
                      aria-hidden="true"
                      className="tw-absolute tw-right-1 tw-top-1 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-warning"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default memo(QuestionPalette);
